import type { ReactNode } from 'react'
import { useContext, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { getDefaultRegionPlace } from 'constants/places'
import { LUXURY_ESCAPES } from 'constants/brands'
import config from 'constants/config'
import { EXP_CATEGORY_TICKETS_AND_PASSES_ID } from 'constants/experience'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import useSmartExperiencePlace from 'components/Experiences/hooks/useSmartExperiencePlace'
import useMostPopulatedSearchExperienceFacets from 'components/Experiences/hooks/useMostPopulatedSearchExperienceFacets'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import { getExperiencePlaceShortName, getExperienceSearchPath } from 'lib/experiences/experienceUtils'
import { LUX_PLUS_MEMBER_PRICES_FILTER } from 'lib/experiences/luxPlusFeatureFilterUtils'
import {
  getExperienceCategoryFacetCount,
  getExperienceCollectionFacetCount,
} from 'lib/experiences/experiencesHomepageUtils'
import { getThisWeekendRange } from 'lib/search/experiences'

import ExperiencesHomepageBrowseTileCarousel from './ExperiencesHomepageBrowseTileCarousel'
import type { BrowseTile } from './ExperiencesHomepageBrowseTileCarousel'

const messages = defineMessages({
  sectionTitle: {
    id: 'EsTPPwriwOTGdJ2wWiTN',
    defaultMessage: 'Explore {placeName}, <i>your way</i>',
    description: 'Section heading for the browse-by-trend carousel on the Experiences homepage uplift, with the user place name',
  },
  viewOffers: {
    id: '0kFVMMe6151X343R-Afz',
    defaultMessage: 'View {count, plural, one {# offer} other {# offers}}',
    description: 'Non-interactive aggregate offers CTA beside browse by category on Experiences homepage uplift',
  },
  luxPlusSpecials: {
    id: '3oBc2qk7h_JN4nkAXKAX',
    defaultMessage: 'LuxPlus+ member only specials',
    description: 'Trend tile title for LuxPlus member only specials on the Experiences homepage uplift browse-by-trend carousel',
  },
  weekendThings: {
    id: 'WiWuFdHyyVB5NnnLNy3Z',
    defaultMessage: 'Things to do this weekend',
    description: 'Trend tile title for things to do this weekend on the Experiences homepage uplift browse-by-trend carousel',
  },
  thingsWithKids: {
    id: 'elA-XSqNuLG9gCWyIO8m',
    defaultMessage: 'Things to do with kids',
    description: 'Trend tile title for family-friendly things to do with kids on the Experiences homepage uplift browse-by-trend carousel',
  },
  mostBooked: {
    id: 'Q11l3XjEuJHFgHqMF1YT',
    defaultMessage: 'Most booked experiences',
    description: 'Trend tile title for most booked experiences on the Experiences homepage uplift browse-by-trend carousel',
  },
  romanticExperiences: {
    id: '2Ozkp5XmF0K-FyH07nq9',
    defaultMessage: 'Romantic experiences',
    description: 'Trend tile title for romantic experiences on the Experiences homepage uplift browse-by-trend carousel',
  },
  seeTheSights: {
    id: 'q-xNj0jTt7pkUBYzj4f7',
    defaultMessage: 'See the sights',
    description: 'Trend tile title for sightseeing experiences on the Experiences homepage uplift browse-by-trend carousel',
  },
})

const TREND_IMAGE_IDS = {
  luxPlus: 'af885b0d-59f3-451c-8605-8605aa18a233',
  weekend: '5576ff09-1efa-47fc-ac44-4d096463c939',
  kids: 'c0b10677-4bd0-4207-adac-a95b51bde390',
  mostBooked: '71126717-f302-4ce6-ac16-47fa48680a1d',
  romantic: '5ddc4193-83dd-457a-a485-e3490f767969',
  seeTheSights: '71126717-f302-4ce6-ac16-47fa48680a1d',
} as const

const THINGS_TO_DO_WITH_KIDS_COLLECTION_ID = 'things-to-do-with-kids'

function ExperiencesHomepageBrowseByTrend() {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const smartPlace = useSmartExperiencePlace()
  const place = smartPlace ?? getDefaultRegionPlace(geo.currentRegionCode)

  const weekendRange = useMemo(() => getThisWeekendRange(), [])
  const weekendFrom = weekendRange.start.format(ISO_DATE_FORMAT)
  const weekendTo = weekendRange.end.format(ISO_DATE_FORMAT)

  const {
    luxPlusMemberPriceCount,
    collections,
    categories,
    fetching: facetsFetching,
  } = useMostPopulatedSearchExperienceFacets(place, {
    includeCollectionsWithoutExperiment: true,
  })

  const weekendList = useExperienceSearchList({
    placeId: place?.id,
    from: weekendFrom,
    to: weekendTo,
    enabled: !!place,
  })

  const mostBookedList = useExperienceSearchList({
    placeId: place?.id,
    sortBy: 'mostBooked',
    enabled: !!place,
  })

  const placeSearchList = useExperienceSearchList({
    placeId: place?.id,
    sortBy: 'type',
    enabled: !!place,
  })

  const romanticCollection = useMemo(
    () => collections.find((collection) => /romantic/i.test(collection.theme)),
    [collections],
  )

  const placeName = getExperiencePlaceShortName(place)
  const fetching = facetsFetching || weekendList.fetching || mostBookedList.fetching || placeSearchList.fetching

  const tiles = useMemo((): Array<BrowseTile> => {
    if (fetching) {
      return []
    }

    const kidsCount = getExperienceCollectionFacetCount(
      collections,
      (collection) => collection.collectionId === THINGS_TO_DO_WITH_KIDS_COLLECTION_ID,
    )
    const seeTheSightsCount = getExperienceCategoryFacetCount(categories, EXP_CATEGORY_TICKETS_AND_PASSES_ID)
    const weekendCount = weekendList.ids.length
    const mostBookedCount = mostBookedList.ids.length
    const romanticCount = romanticCollection?.count ?? 0

    const trendTiles: Array<BrowseTile | undefined> = [
      config.BRAND === LUXURY_ESCAPES && luxPlusMemberPriceCount > 0 ? {
        key: 'luxplus-specials',
        title: intl.formatMessage(messages.luxPlusSpecials),
        offerCount: luxPlusMemberPriceCount,
        image: { id: TREND_IMAGE_IDS.luxPlus, title: intl.formatMessage(messages.luxPlusSpecials) },
        to: getExperienceSearchPath(place, { luxPlusFeatures: [LUX_PLUS_MEMBER_PRICES_FILTER] }),
      } : undefined,
      weekendCount > 0 ? {
        key: 'weekend-things',
        title: intl.formatMessage(messages.weekendThings),
        offerCount: weekendCount,
        image: { id: TREND_IMAGE_IDS.weekend, title: intl.formatMessage(messages.weekendThings) },
        to: getExperienceSearchPath(place, { checkIn: weekendFrom, checkOut: weekendTo }),
      } : undefined,
      kidsCount > 0 ? {
        key: 'things-with-kids',
        title: intl.formatMessage(messages.thingsWithKids),
        offerCount: kidsCount,
        image: { id: TREND_IMAGE_IDS.kids, title: intl.formatMessage(messages.thingsWithKids) },
        to: getExperienceSearchPath(place, { collections: [THINGS_TO_DO_WITH_KIDS_COLLECTION_ID] }),
      } : undefined,
      mostBookedCount > 0 ? {
        key: 'most-booked',
        title: intl.formatMessage(messages.mostBooked),
        image: { id: TREND_IMAGE_IDS.mostBooked, title: intl.formatMessage(messages.mostBooked) },
        to: getExperienceSearchPath(place, { sortBy: 'mostBooked' }),
      } : undefined,
      romanticCollection && romanticCount > 0 ? {
        key: 'romantic-experiences',
        title: intl.formatMessage(messages.romanticExperiences),
        offerCount: romanticCount,
        image: { id: TREND_IMAGE_IDS.romantic, title: intl.formatMessage(messages.romanticExperiences) },
        to: getExperienceSearchPath(place, { collections: [romanticCollection.collectionId] }),
      } : undefined,
      seeTheSightsCount > 0 ? {
        key: 'see-the-sights',
        title: intl.formatMessage(messages.seeTheSights),
        offerCount: seeTheSightsCount,
        image: { id: TREND_IMAGE_IDS.seeTheSights, title: intl.formatMessage(messages.seeTheSights) },
        to: getExperienceSearchPath(place, { categories: [EXP_CATEGORY_TICKETS_AND_PASSES_ID.toString()] }),
      } : undefined,
    ]

    return trendTiles.filter((tile): tile is BrowseTile => tile !== undefined)
  }, [
    categories,
    collections,
    fetching,
    intl,
    luxPlusMemberPriceCount,
    mostBookedList.ids.length,
    place,
    romanticCollection,
    weekendFrom,
    weekendList.ids.length,
    weekendTo,
  ])

  const totalOfferCount = placeSearchList.fetching ? 0 : placeSearchList.ids.length

  const searchUrl = useMemo(() => getExperienceSearchPath(place), [place])

  if (!fetching && tiles.length === 0) {
    return null
  }

  return (
    <ExperiencesHomepageBrowseTileCarousel
      title={intl.formatMessage(messages.sectionTitle, {
        placeName,
        i: (chunks: ReactNode) => <i key="italic">{chunks}</i>,
      })}
      actionLabel={!fetching ? intl.formatMessage(messages.viewOffers, { count: totalOfferCount }) : undefined}
      actionUrl={searchUrl}
      tiles={tiles}
      isLoading={fetching}
    />
  )
}

export default ExperiencesHomepageBrowseByTrend
